import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from "./helpers/apiErrorMessage";

function Profile() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({ username: '' });
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showFollowersModal, setShowFollowersModal] = useState(false);
    const [showFollowingModal, setShowFollowingModal] = useState(false);
    const [showAllUsersModal, setShowAllUsersModal] = useState(false);
    const [followers, setFollowers] = useState([]);
    const [following, setFollowing] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [error, setError] = useState(null);
    const [favouriteAlbums, setFavouriteAlbums] = useState([]);
    const [showFavModal, setShowFavModal] = useState(false);
    const [favSlot, setFavSlot] = useState(null);
    const [favQuery, setFavQuery] = useState('');
    const [favSearchResult, setFavSearchResult] = useState(null);
    const [favSearchError, setFavSearchError] = useState('');
    const [favSearching, setFavSearching] = useState(false);

    useEffect(() => {
        fetchUserProfile();
        fetchUserStatistics();
        fetchFavouriteAlbums();
    }, []);

    const fetchUserProfile = async () => {
        try {
            const res = await axios.get('/api/auth/check-login');
            if (res.data.loggedIn) {
                setUser(res.data);
                setEditForm({ username: res.data.username });
            }
            setLoading(false);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load your profile'));
            console.error('Error fetching profile:', error);
            setLoading(false);
        }
    };

    const fetchUserStatistics = async () => {
        try {
            const res = await axios.get('/api/auth/statistics');
            setStatistics(res.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load statistics'));
            console.error('Error fetching statistics:', error);
        }
    };

    const fetchFavouriteAlbums = async () => {
        try {
            const res = await axios.get('/api/auth/favourite-albums');
            setFavouriteAlbums(res.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load favourite albums'));
            console.error('Error fetching favourite albums:', error);
        }
    };

    const openFavSlot = (position) => {
        setFavSlot(position);
        setFavQuery('');
        setFavSearchResult(null);
        setFavSearchError('');
        setShowFavModal(true);
    };

    const searchFavAlbum = async () => {
        if (!favQuery.trim()) return;
        setFavSearching(true);
        setFavSearchError('');
        setFavSearchResult(null);
        try {
            const res = await axios.get(`/api/search?query=${encodeURIComponent(favQuery)}`);
            const results = Array.isArray(res.data) ? res.data : res.data?.results || [];
            if (!results.length) {
                setFavSearchError('Album not found');
                return;
            }
            setFavSearchResult(results[0]);
        } catch (error) {
            setFavSearchError('Album not found');
        } finally {
            setFavSearching(false);
        }
    };

    const addFavouriteAlbum = async () => {
        if (!favSearchResult) return;
        try {
            await axios.post('/api/auth/favourite-albums', {
                mbid: favSearchResult.mbid,
                position: favSlot,
                title: favSearchResult.title,
                artist: favSearchResult.artist,
                releaseDate: favSearchResult.releaseDate,
                albumCover: favSearchResult.albumCover,
                country: favSearchResult.country,
                duration: favSearchResult.duration,
                tags: favSearchResult.tags || []
            });
            setShowFavModal(false);
            fetchFavouriteAlbums();
        } catch (error) {
            setFavSearchError(error.response?.data?.message || 'Could not add album');
        }
    };

    const removeFavouriteAlbum = async (mbid) => {
        try {
            await axios.delete(`/api/auth/favourite-albums/${mbid}`);
            fetchFavouriteAlbums();
        } catch (error) {
            setError(getApiErrorMessage(error, 'remove favourite album'));
        }
    };

    const fetchFollowers = async () => {
        try {
            const res = await axios.get('/api/auth/followers');
            setFollowers(res.data);
            setShowFollowersModal(true);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load followers'));
            console.error('Error fetching followers:', error);
        }
    };

    const fetchFollowing = async () => {
        try {
            const res = await axios.get('/api/auth/following');
            setFollowing(res.data);
            setShowFollowingModal(true);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load following'));
            console.error('Error fetching following:', error);
        }
    };

    const fetchAllUsers = async () => {
        try {
            const res = await axios.get('/api/auth/all-users');
            setAllUsers(res.data);
            setShowAllUsersModal(true);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load users'));
            console.error('Error fetching all users:', error);
        }
    };

    const updateFollowingData = async () => {
        try {
            const res = await axios.get('/api/auth/following');
            setFollowing(res.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'refresh following'));
            console.error('Error fetching following:', error);
        }
    };

    const updateAllUsersData = async () => {
        try {
            const res = await axios.get('/api/auth/all-users');
            setAllUsers(res.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'refresh users'));
            console.error('Error fetching all users:', error);
        }
    };

    const fetchSelectedUserProfile = async (userId) => {
        navigate(`/profile/${userId}`);
    };

    const handleFollow = async (userId) => {
        try {
            await axios.post(`/api/auth/follow/${userId}`);
            updateAllUsersData();
            updateFollowingData();
            fetchUserStatistics();
        } catch (error) {
            setError(getApiErrorMessage(error, 'follow this user'));
            console.error('Error following user:', error);
        }
    };

    const handleUnfollow = async (userId) => {
        try {
            await axios.post(`/api/auth/unfollow/${userId}`);
            updateAllUsersData();
            updateFollowingData();
            fetchUserStatistics();
        } catch (error) {
            setError(getApiErrorMessage(error, 'unfollow this user'));
            console.error('Error unfollowing user:', error);
        }
    };

    const handleEditChange = (e) => {
        setEditForm({ ...editForm, [e.target.name]: e.target.value });
    };

    const handleSaveProfile = async () => {
        try {
            const res = await axios.post('/api/auth/update-profile', editForm);
            setMessage(res.data.message);
            setIsError(false);
            setIsEditing(false);
            setUser({ ...user, username: editForm.username });
        } catch (error) {
            setMessage(error.response?.data?.message || 'Error updating profile');
            setIsError(true);
            console.error(error);
        }
    };

    const handleCancel = () => {
        setEditForm({ username: user.username });
        setIsEditing(false);
        setMessage('');
        setIsError(false);
    };

    if (loading) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Loading...</div>;
    }

    if (!user) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">User not found</div>;
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white px-6 pb-10">
            {error && (
                <div className="bg-red-900 border-l-4 border-red-600 p-4 m-4 rounded">
                    <div className="flex justify-between items-center">
                        <p className="text-red-200">{error}</p>
                        <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200 text-2xl">×</button>
                    </div>
                </div>
            )}
            <div className="max-w-2xl mx-auto">
                <h1 className="text-4xl font-bold mb-10">My Profile</h1>

                {/* Profile Card */}
                <div className="bg-gray-800 rounded-lg p-8 mb-8">
                    {/* Avatar Section */}
                    <div className="flex items-center gap-6 mb-8">
                        <div className="w-24 h-24 bg-green-600 rounded-full flex items-center justify-center text-4xl font-bold">
                            {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold">{user.username}</h2>
                            <p className="text-gray-400 capitalize mt-2">Role: {user.role}</p>
                        </div>
                    </div>

                    {/* Profile Information */}
                    {!isEditing ? (
                        <div className="space-y-4 mb-6">
                            <div className="bg-gray-700 p-4 rounded">
                                <p className="text-gray-400 text-sm">Username</p>
                                <p className="text-white text-lg">{user.username}</p>
                            </div>
                            <div className="bg-gray-700 p-4 rounded">
                                <p className="text-gray-400 text-sm">Account Type</p>
                                <p className="text-white text-lg capitalize">{user.role}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="text-gray-400 text-sm block mb-2">Username</label>
                                <input
                                    type="text"
                                    name="username"
                                    value={editForm.username}
                                    onChange={handleEditChange}
                                    className="w-full bg-gray-700 text-white px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                        {!isEditing ? (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded transition"
                            >
                                Edit Profile
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={handleSaveProfile}
                                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded transition"
                                >
                                    Save Changes
                                </button>
                                <button
                                    onClick={handleCancel}
                                    className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded transition"
                                >
                                    Cancel
                                </button>
                            </>
                        )}
                    </div>

                    {/* Message */}
                    {message && (
                        <p className={`mt-4 text-sm ${isError ? 'text-red-400' : 'text-green-400'}`}>{message}</p>
                    )}
                </div>

                {/* Statistics Section */}
                {statistics && (
                    <div className="bg-gray-800 rounded-lg p-8 mb-8">
                        <h3 className="text-xl font-bold mb-6">My Statistics</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                            <div className="bg-gray-700 p-4 rounded-lg text-center">
                                <div className="text-2xl font-bold text-green-400">{statistics.uniqueAlbums}</div>
                                <div className="text-sm text-gray-300">Listened Albums</div>
                            </div>
                            <div className="bg-gray-700 p-4 rounded-lg text-center">
                                <div className="text-2xl font-bold text-green-400">{statistics.totalEntries}</div>
                                <div className="text-sm text-gray-300">Diary Entries</div>
                            </div>
                            <div className="bg-gray-700 p-4 rounded-lg text-center">
                                <div className="text-2xl font-bold text-green-400">{statistics.likedEntries}</div>
                                <div className="text-sm text-gray-300">Liked Entries</div>
                            </div>
                            <div className="bg-gray-700 p-4 rounded-lg text-center">
                                <div className="text-2xl font-bold text-green-400">{statistics.wishlistEntries}</div>
                                <div className="text-sm text-gray-300">Wishlisted Albums</div>
                            </div>
                            <div className="bg-gray-700 p-4 rounded-lg text-center">
                                <div className="text-2xl font-bold text-green-400">{statistics.totalLists}</div>
                                <div className="text-sm text-gray-300">Lists</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-6">
                            <button
                                onClick={fetchFollowers}
                                className="bg-gray-700 hover:bg-gray-600 p-4 rounded-lg text-center transition cursor-pointer"
                            >
                                <div className="text-2xl font-bold text-green-400">{statistics.followers}</div>
                                <div className="text-sm text-gray-300">Followers</div>
                            </button>
                            <button
                                onClick={fetchFollowing}
                                className="bg-gray-700 hover:bg-gray-600 p-4 rounded-lg text-center transition cursor-pointer"
                            >
                                <div className="text-2xl font-bold text-green-400">{statistics.following}</div>
                                <div className="text-sm text-gray-300">Following</div>
                            </button>
                            <button
                                onClick={fetchAllUsers}
                                className="bg-green-600 hover:bg-green-700 p-4 rounded-lg text-center transition cursor-pointer font-semibold"
                            >
                                <div className="text-sm text-white">+ Add</div>
                                <div className="text-sm text-gray-200">Friends</div>
                            </button>
                        </div>
                    </div>
                )}

                {/* Favourite Albums */}
                <div className="bg-gray-800 rounded-lg p-8 mb-8">
                    <h3 className="text-xl font-bold mb-6">Favourite Albums</h3>
                    <div className="grid grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map((slot) => {
                            const album = favouriteAlbums.find(a => a.position === slot);
                            return album ? (
                                <div key={slot} className="relative group">
                                    <div className="aspect-square bg-gray-700 rounded-lg overflow-hidden">
                                        {album.albumCover ? (
                                            <img src={album.albumCover} alt={album.title} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs text-center p-2">
                                                {album.title}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-2">
                                        <p className="text-white text-xs font-semibold truncate">{album.title}</p>
                                        <p className="text-gray-400 text-xs truncate">{album.artist}</p>
                                    </div>
                                    <button
                                        onClick={() => removeFavouriteAlbum(album.mbid)}
                                        className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                </div>
                            ) : (
                                <button
                                    key={slot}
                                    onClick={() => openFavSlot(slot)}
                                    className="aspect-square bg-gray-700 hover:bg-gray-600 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:text-gray-200 transition border-2 border-dashed border-gray-600 hover:border-green-500"
                                >
                                    <span className="text-2xl">+</span>
                                    <span className="text-xs mt-1">Add album</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Additional Info */}
                <div className="bg-gray-800 rounded-lg p-8">
                    <h3 className="text-xl font-bold mb-4">Account Information</h3>
                    <div className="space-y-4 text-gray-300">
                        <p>Manage your profile settings and preferences here.</p>
                        <p className="text-sm">For security updates or password changes, please use the account settings.</p>
                    </div>
                </div>
            </div>

            {/* Followers Modal */}
            {showFollowersModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto">
                        <div className="sticky top-0 bg-gray-800 p-6 border-b border-gray-700 flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-white">Followers</h2>
                            <button
                                onClick={() => setShowFollowersModal(false)}
                                className="text-gray-400 hover:text-white text-2xl"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {followers.length === 0 ? (
                                <p className="text-gray-400">No followers yet</p>
                            ) : (
                                followers.map((follower) => (
                                    <div
                                        key={follower.userId}
                                        className="bg-gray-700 p-4 rounded-lg flex justify-between items-center hover:bg-gray-600 cursor-pointer transition"
                                        onClick={() => fetchSelectedUserProfile(follower.userId)}
                                    >
                                        <span className="text-white font-semibold">{follower.username}</span>
                                        <i className="fas fa-arrow-right text-gray-400"></i>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Following Modal */}
            {showFollowingModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto">
                        <div className="sticky top-0 bg-gray-800 p-6 border-b border-gray-700 flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-white">Following</h2>
                            <button
                                onClick={() => setShowFollowingModal(false)}
                                className="text-gray-400 hover:text-white text-2xl"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {following.length === 0 ? (
                                <p className="text-gray-400">Not following anyone yet</p>
                            ) : (
                                following.map((followingUser) => (
                                    <div
                                        key={followingUser.userId}
                                        className="bg-gray-700 p-4 rounded-lg flex justify-between items-center hover:bg-gray-600 cursor-pointer transition"
                                        onClick={() => fetchSelectedUserProfile(followingUser.userId)}
                                    >
                                        <span className="text-white font-semibold">{followingUser.username}</span>
                                        <i className="fas fa-arrow-right text-gray-400"></i>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* All Users Modal */}
            {showAllUsersModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto">
                        <div className="sticky top-0 bg-gray-800 p-6 border-b border-gray-700 flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-white">Discover Users</h2>
                            <button
                                onClick={() => setShowAllUsersModal(false)}
                                className="text-gray-400 hover:text-white text-2xl"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {allUsers.length === 0 ? (
                                <p className="text-gray-400">No other users available</p>
                            ) : (
                                allUsers.map((u) => (
                                    <div key={u.userId} className="bg-gray-700 p-4 rounded-lg flex justify-between items-center">
                                        <span
                                            className="text-white font-semibold cursor-pointer hover:text-green-400 transition"
                                            onClick={() => fetchSelectedUserProfile(u.userId)}
                                        >
                                            {u.username}
                                        </span>
                                        {u.isFollowing ? (
                                                <button
                                                    onClick={() => handleUnfollow(u.userId)}
                                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition"
                                            >
                                                Unfollow
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleFollow(u.userId)}
                                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition"
                                            >
                                                Follow
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Favourite Album Search Modal */}
            {showFavModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-lg w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-white">Add to Slot {favSlot}</h2>
                            <button
                                onClick={() => setShowFavModal(false)}
                                className="text-gray-400 hover:text-white text-2xl"
                            >
                                ×
                            </button>
                        </div>

                        <div className="flex gap-2 mb-4">
                            <input
                                type="text"
                                value={favQuery}
                                onChange={e => setFavQuery(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && searchFavAlbum()}
                                placeholder="Search album..."
                                className="flex-1 bg-gray-700 text-white placeholder-gray-400 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <button
                                onClick={searchFavAlbum}
                                disabled={favSearching}
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition disabled:opacity-50"
                            >
                                {favSearching ? '...' : 'Search'}
                            </button>
                        </div>

                        {favSearchError && (
                            <p className="text-red-400 text-sm mb-4">{favSearchError}</p>
                        )}

                        {favSearchResult && (
                            <div className="bg-gray-700 rounded-lg overflow-hidden mb-4">
                                {favSearchResult.albumCover ? (
                                    <img
                                        src={favSearchResult.albumCover}
                                        alt={favSearchResult.title}
                                        className="w-full aspect-square object-cover"
                                    />
                                ) : (
                                    <div className="w-full aspect-square bg-gray-600 flex items-center justify-center text-gray-400 text-sm">
                                        No cover art
                                    </div>
                                )}
                                <div className="p-4">
                                    <p className="text-white font-semibold">{favSearchResult.title}</p>
                                    <p className="text-gray-400 text-sm">{favSearchResult.artist}</p>
                                    {favSearchResult.releaseDate && (
                                        <p className="text-gray-500 text-xs mt-1">{favSearchResult.releaseDate}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowFavModal(false)}
                                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addFavouriteAlbum}
                                disabled={!favSearchResult}
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition disabled:opacity-50"
                            >
                                Add to Favourites
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Profile;
